import React, { useState } from "react";
import { useDispatch } from "react-redux";

import AuthenticatorSetup from "../component/AuthenticatorSetup";
import { startSession } from "../store/admin/action";
import { authApi, trustedDevice } from "../util/adminAuthApi";
import { Toast } from "../util/Toast";

// Admin sign-in. The server decides each next step:
//   password -> mfa (authenticator code, optional "remember 7 days") -> signed in
//   password (temporary) -> change_password -> setup_authenticator -> back to sign in
// No session exists until the authenticator code (or a remembered device) passes.
const LoginPage = () => {
  const dispatch = useDispatch();
  const [step, setStep] = useState("password");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [ticket, setTicket] = useState("");
  const [setup, setSetup] = useState(null);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (err) {
      setError("Unable to reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const backToSignIn = (message = "") => {
    setStep("password");
    setPassword("");
    setTicket("");
    setSetup(null);
    setTemporaryPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setCode("");
    setRemember(false);
    setNotice(message);
  };

  const signedIn = (result) => {
    dispatch(startSession(result.token, result.admin));
    if (result.deviceToken) trustedDevice.set(username, result.deviceToken);
    Toast("success", "Signed in.");
    window.location.href = "/admin/dashboard";
  };

  // The server's answer always names the next step.
  const goTo = (result) => {
    switch (result.next) {
      case "signed_in":
        return signedIn(result);
      case "mfa":
        // A stored device token was not accepted (expired or revoked).
        trustedDevice.forget(username);
        setTicket(result.challenge);
        return setStep("mfa");
      case "change_password":
        setTicket(result.ticket);
        setTemporaryPassword("");
        setPassword("");
        return setStep("change_password");
      case "setup_authenticator":
        setTicket(result.ticket);
        setSetup(result.setup);
        setCode("");
        return setStep("setup_authenticator");
      default:
        return setError("Unexpected response. Please try again.");
    }
  };

  const submitPassword = (event) => {
    event.preventDefault();
    if (!username.trim() || !password)
      return setError("Enter your username and password.");
    setNotice("");
    run(async () => {
      const result = await authApi.login(
        username.trim(),
        password,
        trustedDevice.get(username),
      );
      if (!result.status) return setError(result.message);
      goTo(result);
    });
  };

  const submitNewPassword = (event) => {
    event.preventDefault();
    if (!temporaryPassword || !newPassword)
      return setError("Fill in every field.");
    if (newPassword !== confirmPassword)
      return setError("The new passwords do not match.");
    run(async () => {
      const result = await authApi.setInitialPassword(
        ticket,
        temporaryPassword,
        newPassword,
      );
      if (!result.status) {
        if (/sign in again/i.test(result.message || "")) backToSignIn();
        return setError(result.message);
      }
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTemporaryPassword("");
      goTo(result);
    });
  };

  const submitSetupCode = (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code.replace(/\s/g, "")))
      return setError("Enter the 6-digit code.");
    run(async () => {
      const result = await authApi.setupAuthenticator(
        ticket,
        code.replace(/\s/g, ""),
      );
      setCode("");
      if (!result.status) {
        if (/sign in again/i.test(result.message || "")) backToSignIn();
        return setError(result.message);
      }
      backToSignIn("Your account is ready. Sign in with your new password.");
    });
  };

  const submitCode = (event) => {
    event.preventDefault();
    const value = code.trim();
    if (!value) return setError("Enter the 6-digit code.");
    run(async () => {
      const result = await authApi.verifyMfa(ticket, {
        code: value.replace(/\s/g, ""),
        rememberDevice: remember,
      });
      if (!result.status) {
        setCode("");
        if (/start again/i.test(result.message || "")) backToSignIn();
        return setError(result.message);
      }
      signedIn(result);
    });
  };

  const titles = {
    password: "VibeLynk Admin",
    change_password: "Set your password",
    setup_authenticator: "Register your authenticator",
    mfa: "VibeLynk Admin",
  };

  return (
    <div className="login-page back__style">
      <div className="container">
        <div className="row justify-content-md-center">
          <div
            className={
              step === "setup_authenticator"
                ? "col-md-12 col-lg-5"
                : "col-md-12 col-lg-4"
            }
          >
            <div className="card login-box-container">
              <div className="card-body">
                {step === "password" && (
                  <img
                    alt="VibeLynk"
                    style={{
                      height: 70,
                      width: 70,
                      borderRadius: 10,
                      display: "flex",
                      margin: "auto",
                    }}
                    src="/vibelynk_logo.png"
                  />
                )}
                <div className="authent-logo mb-4 mt-2">
                  <span className="text-danger h3">{titles[step]}</span>
                </div>

                {notice && (
                  <div className="alert alert-success py-2" role="status">
                    {notice}
                  </div>
                )}
                {error && (
                  <div className="alert alert-danger py-2" role="alert">
                    {error}
                  </div>
                )}

                {step === "password" && (
                  <form onSubmit={submitPassword} autoComplete="on">
                    <div className="mb-3">
                      <label className="form-label" htmlFor="username">
                        Username
                      </label>
                      <input
                        id="username"
                        className="form-control"
                        autoComplete="username"
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="password">
                        Password
                      </label>
                      <input
                        id="password"
                        type="password"
                        className="form-control"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <div className="d-grid">
                      <button
                        className="btn btn-danger m-b-xs"
                        type="submit"
                        disabled={busy}
                      >
                        {busy ? "Checking…" : "Continue"}
                      </button>
                    </div>
                  </form>
                )}

                {step === "change_password" && (
                  <form onSubmit={submitNewPassword} autoComplete="off">
                    <div className="mb-3">
                      <label className="form-label" htmlFor="temporaryPassword">
                        Temporary password
                      </label>
                      <input
                        id="temporaryPassword"
                        type="password"
                        className="form-control"
                        autoComplete="current-password"
                        value={temporaryPassword}
                        onChange={(e) => setTemporaryPassword(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="newPassword">
                        New password
                      </label>
                      <input
                        id="newPassword"
                        type="password"
                        className="form-control"
                        autoComplete="new-password"
                        autoFocus
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="confirmPassword">
                        Confirm new password
                      </label>
                      <input
                        id="confirmPassword"
                        type="password"
                        className="form-control"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </div>
                    <div className="d-grid">
                      <button
                        className="btn btn-danger"
                        type="submit"
                        disabled={busy}
                      >
                        {busy ? "Saving…" : "Save password"}
                      </button>
                    </div>
                  </form>
                )}

                {step === "setup_authenticator" && setup && (
                  <form onSubmit={submitSetupCode} autoComplete="off">
                    <AuthenticatorSetup setup={setup} />
                    <div className="mb-3">
                      <label className="form-label" htmlFor="setupCode">
                        Code from your authenticator
                      </label>
                      <input
                        id="setupCode"
                        className="form-control"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={7}
                        placeholder="123456"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                      />
                    </div>
                    <div className="d-grid">
                      <button
                        className="btn btn-danger"
                        type="submit"
                        disabled={busy}
                      >
                        {busy ? "Verifying…" : "Register authenticator"}
                      </button>
                    </div>
                  </form>
                )}

                {step === "mfa" && (
                  <form onSubmit={submitCode} autoComplete="off">
                    <div className="mb-3">
                      <label className="form-label" htmlFor="code">
                        Authenticator code
                      </label>
                      <input
                        id="code"
                        className="form-control"
                        autoFocus
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={7}
                        placeholder="123456"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                      />
                    </div>
                    <div className="form-check mb-3">
                      <input
                        id="remember"
                        className="form-check-input"
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="remember">
                        Don't ask for a code on this device for 7 days
                      </label>
                    </div>
                    <div className="d-grid">
                      <button
                        className="btn btn-danger m-b-xs"
                        type="submit"
                        disabled={busy}
                      >
                        {busy ? "Verifying…" : "Sign in"}
                      </button>
                    </div>
                    <div className="d-flex justify-content-end mt-2">
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => backToSignIn()}
                      >
                        Start over
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
